import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Router} from '@angular/router';
import {FormBuilder, FormGroup, NgForm, Validators} from '@angular/forms';
import {MatSnackBar} from '@angular/material/snack-bar';
import {HttpErrorResponse} from '@angular/common/http';
import {of, ReplaySubject} from 'rxjs';
import {concatMap, map, takeUntil} from 'rxjs/operators';
import {AngularEditorConfig} from '@kolkov/angular-editor';
import {DobryAgentBlogCategory} from '../../../../shared/interface/dobry-agent-blog-article';
import {DobryAgentBlogCategoryService} from '../../../../shared/services/dobry-agent-blog-category.service';
import {Uuid} from '../../../../shared/interface/response/uuid';
import {MatDialog} from "@angular/material/dialog";
import {ConfirmDialogComponent} from "../../../../shared/components/confirm-dialog/confirm-dialog.component";

@Component({
  selector: 'app-da-category-add',
  templateUrl: './da-category-add.component.html',
  styleUrls: ['./da-category-add.component.scss']
})
export class DaCategoryAddComponent implements OnInit, OnChanges, OnDestroy {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @Input() blogCategory?: DobryAgentBlogCategory;
  @Input() mod = false;

  @Output() closeModification = new EventEmitter<void>();

  uuid?: string;
  isEditMode = false;
  loading = false;
  saving = false;

  categoryReactiveForm: FormGroup;
  selectedIconFile: File | null = null;
  selectedIconFileName = '';
  deletingIcon = false;

  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: '220px',
    minHeight: '160px',
    maxHeight: '400px',
    width: '100%',
    minWidth: '0',
    translate: 'yes',
    enableToolbar: true,
    showToolbar: true,
    placeholder: 'Wpisz opis kategorii...',
    defaultParagraphSeparator: 'p',
    defaultFontName: 'Arial',
    sanitize: false,
    toolbarHiddenButtons: [
      [
        'strikeThrough',
        'subscript',
        'superscript',
        'justifyRight',
        'justifyFull',
        'indent',
        'outdent',
        'insertUnorderedList',
        'insertOrderedList',
        'heading',
        'fontName',
        'fontSize',
        'textColor',
        'backgroundColor',
        'customClasses',
        'link',
        'unlink',
        'insertImage',
        'insertVideo',
      ],
      []
    ]
  };

  model: Partial<DobryAgentBlogCategory> = {
    name: '',
    altImage: '',
    position: undefined,
    seoSlug: '',
    seoTitle: '',
    seoDescription: '',
    seoKeywords: '',
    icon: '',
    active: true,
  };

  constructor(
    private router: Router,
    private snackBar: MatSnackBar,
    private blogCategoryService: DobryAgentBlogCategoryService,
    private fb: FormBuilder,
    private dialog: MatDialog,
  ) {
    this.categoryReactiveForm = this.fb.group({
      description: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    if (this.mod && this.blogCategory) {
      this.setModel(this.blogCategory);
      return;
    }

    this.isEditMode = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.blogCategory && this.blogCategory) {
      this.setModel(this.blogCategory);
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  save(form: NgForm, addNext = false): void {
    if (form.invalid || this.categoryReactiveForm.invalid || this.saving) {
      form.control.markAllAsTouched();
      this.categoryReactiveForm.markAllAsTouched();
      return;
    }

    this.saving = true;

    const formData = {
      name: this.model.name,
      altImage: this.model.altImage,
      position: this.model.position,
      description: this.categoryReactiveForm.get('description')?.value,
      seoSlug: this.model.seoSlug,
      seoTitle: this.model.seoTitle,
      seoDescription: this.model.seoDescription,
      seoKeywords: this.model.seoKeywords,
      active: !!this.model.active,
    };

    this.blogCategoryService.send(formData, this.uuid)
      .pipe(
        takeUntil(this.destroyed$),
        concatMap((result: Uuid) => {
          if (this.selectedIconFile && result?.uuid) {
            return this.blogCategoryService
              .uploadIcon(this.selectedIconFile, result.uuid)
              .pipe(map(() => result));
          }

          return of(result);
        })
      )
      .subscribe({
        next: (result: Uuid) => {
          this.saving = false;

          this.snackBar.open(
            this.isEditMode ? 'Kategoria została zapisana.' : 'Kategoria została dodana.',
            'Zamknij',
            {duration: 3000}
          );

          this.selectedIconFile = null;
          this.selectedIconFileName = '';

          if (this.mod) {
            this.closeModification.emit();
            return;
          }

          if (addNext) {
            this.resetForm(form);
            return;
          }

          if (result?.uuid) {
            this.router.navigate(['/blog-dobry-agent/kategorie/szczegoly', result.uuid]);
            return;
          }

          this.router.navigate(['/blog-dobry-agent/kategorie']);
        },
        error: (error: HttpErrorResponse) => {
          this.saving = false;
          this.handleBackendError(error, form);
        }
      });
  }

  cancel(): void {
    if (this.mod) {
      this.closeModification.emit();
      return;
    }

    this.router.navigate(['/blog-dobry-agent/kategorie']);
  }

  clearBackendError(form: NgForm, fieldName: string): void {
    const control = form.controls[fieldName];

    if (!control?.errors?.backend) {
      return;
    }

    const errors = {...control.errors};
    delete errors.backend;

    control.setErrors(Object.keys(errors).length ? errors : null);
  }

  clearDescriptionBackendError(): void {
    const control = this.categoryReactiveForm.get('description');

    if (!control?.errors?.backend) {
      return;
    }

    const errors = {...control.errors};
    delete errors.backend;

    control.setErrors(Object.keys(errors).length ? errors : null);
  }

  private handleBackendError(error: HttpErrorResponse, form: NgForm): void {
    const backendError = error.error?.error;
    const errorList = backendError?.list;

    if (errorList) {
      Object.keys(errorList).forEach((fieldName: string) => {
        const fieldErrors = errorList[fieldName];

        if (fieldName === 'description' && fieldErrors?.length) {
          this.categoryReactiveForm.get('description')?.setErrors({
            backend: fieldErrors[0]
          });

          this.categoryReactiveForm.get('description')?.markAsTouched();
          return;
        }

        if (form.controls[fieldName] && fieldErrors?.length) {
          form.controls[fieldName].setErrors({
            backend: fieldErrors[0]
          });

          form.controls[fieldName].markAsTouched();
        }
      });

      this.snackBar.open(
        backendError?.text || 'Formularz zawiera błędy.',
        'Zamknij',
        {duration: 4000}
      );

      return;
    }

    this.snackBar.open(
      backendError?.text || 'Nie udało się zapisać kategorii. Sprawdź formularz i spróbuj ponownie.',
      'Zamknij',
      {duration: 4000}
    );
  }

  private setModel(category: DobryAgentBlogCategory): void {
    this.uuid = category.uuid;
    this.isEditMode = true;

    this.model = {
      uuid: category.uuid,
      name: category.name ?? '',
      altImage: category.altImage ?? '',
      position: category.position ?? undefined,
      seoSlug: category.seoSlug ?? '',
      seoTitle: category.seoTitle ?? '',
      seoDescription: category.seoDescription ?? '',
      seoKeywords: category.seoKeywords ?? '',
      active: category.active ?? false,
      icon: category.icon ?? '',
    };

    this.categoryReactiveForm.patchValue({
      description: category.description ?? '',
    });
  }

  private resetForm(form: NgForm): void {
    this.uuid = undefined;
    this.isEditMode = false;

    this.model = {
      name: '',
      altImage: '',
      position: undefined,
      seoSlug: '',
      seoTitle: '',
      seoDescription: '',
      seoKeywords: '',
      active: true,
    };

    this.selectedIconFile = null;
    this.selectedIconFileName = '';

    form.resetForm(this.model);

    this.categoryReactiveForm.reset({
      description: '',
    });
  }

  onIconSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files || !input.files.length) {
      this.selectedIconFile = null;
      this.selectedIconFileName = '';
      return;
    }

    const file = input.files[0];

    if (!file.type.startsWith('image/')) {
      this.selectedIconFile = null;
      this.selectedIconFileName = '';

      this.snackBar.open(
        'Wybrany plik nie jest obrazkiem.',
        'Zamknij',
        {duration: 4000}
      );

      input.value = '';
      return;
    }

    this.selectedIconFile = file;
    this.selectedIconFileName = file.name;
  }
  clearSelectedIcon(iconInput: HTMLInputElement): void {
    this.selectedIconFile = null;
    this.selectedIconFileName = '';
    iconInput.value = '';
  }
  deleteCurrentIcon(): void {
    if (
      !this.uuid
      || !this.model.icon
      || this.deletingIcon
    ) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {
        text: 'Czy na pewno chcesz usunąć ikonę kategorii?'
      },
    });

    dialogRef.afterClosed()
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe((confirmed: boolean) => {
        if (!confirmed) {
          return;
        }

        this.deletingIcon = true;

        this.blogCategoryService
          .deleteIcon(this.uuid)
          .pipe(
            takeUntil(this.destroyed$)
          )
          .subscribe({
            next: () => {
              this.deletingIcon = false;

              this.model.icon = '';

              this.snackBar.open(
                'Ikona kategorii została usunięta.',
                'Zamknij',
                {duration: 3000}
              );
            },
            error: (error: HttpErrorResponse) => {
              this.deletingIcon = false;

              this.snackBar.open(
                error.error?.message
                ?? 'Nie udało się usunąć ikony kategorii.',
                'Zamknij',
                {duration: 4000}
              );
            }
          });
      });
  }
}
